import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import {
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  TextField,
  useTheme
} from '@mui/material';
import { Visitor } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { useRef, useState } from 'react';
import { useKeyManagerSearch } from 'v2/hooks/useSearch/useKeyManagerSearch';

interface Props {
  members: Visitor[];
  onSelect: (member: Visitor) => void;
}

const AddKeyManagerSearch = ({ members, onSelect }: Props) => {
  const theme = useTheme();
  const { translation } = useTranslation();
  const [filterStr, setFilterStr] = useState('');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const searchFieldRef = useRef<HTMLDivElement>(null);

  const filtered = useKeyManagerSearch({ members, search: filterStr });

  const handleClose = () => setAnchorEl(null);

  const handleSelect = (member: Visitor) => {
    onSelect(member);
    handleClose();
  };

  return (
    <>
      <TextField
        id="search-input"
        ref={searchFieldRef}
        variant="outlined"
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon
                  sx={{
                    fill: filterStr !== '' ? theme.palette.text.primary : theme.palette.text.secondary
                  }}
                />
              </InputAdornment>
            ),
            endAdornment:
              filterStr !== '' ? (
                <InputAdornment position="end">
                  <IconButton id="clear-search-button" onClick={() => setFilterStr('')} aria-label={translation.clear}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ) : undefined
          }
        }}
        placeholder={translation.search_user}
        onChange={(event) => setFilterStr(event.target.value)}
        value={filterStr}
        sx={{ width: '100%' }}
        onFocus={() => setAnchorEl(searchFieldRef.current)}
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        sx={{}}
        slotProps={{
          paper: {
            sx: {
              maxHeight: '320px',
              overflowY: 'auto',
              width: '386px'
            }
          }
        }}
      >
        <List dense>
          {filtered.length > 0 ? (
            filtered.map((member, index) => (
              <ListItemButton id={`search-result-row-${index}`} key={member.id} onClick={() => handleSelect(member)}>
                <ListItemText primary={member.name} />
              </ListItemButton>
            ))
          ) : (
            <ListItemText primary={translation.no_members_available_for_key_manager} sx={{ padding: '8px' }} />
          )}
        </List>
      </Popover>
    </>
  );
};

export default AddKeyManagerSearch;
